import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { enumEntityModelCode } from '@app/data-layer/enums/entities';
import { enumEntityType } from '@app/data-layer/enums/isf';
import { AddressModel, ShipmentModel } from '@app/data-layer/models/common';
import { EntityModel } from '@app/data-layer/models/entities';
import { IsfModel } from '@app/data-layer/models/isf';
import { ShipmentsV2Repository } from '@app/data-layer/repositories';
import { FormHelper } from '@helpers/form.helper';
import { UtilsHelper } from '@helpers/utils-helper';
import { ISectionFormField } from '@interfaces/section-form-field';
import { GenericService } from '@services/generic.service';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { IsfOcrQuickFixService, IsfStateService } from '@shared/isf-mutual/services';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { merge, of, ReplaySubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-isf-ocr-quick-fix-consolidator-details',
  templateUrl: './isf-ocr-quick-fix-consolidator-details.component.html',
  styleUrls: ['./isf-ocr-quick-fix-consolidator-details.component.scss'],
})
export class IsfOcrQuickFixConsolidatorDetailsComponent implements OnInit, OnDestroy {
  @ViewChild(MissingFieldsComponent, { static: false }) public missingFieldsModal: MissingFieldsComponent;
  @Input() public consolidatorFields: ISectionFormField[];

  @Output() public dataSaved: EventEmitter<void> = new EventEmitter();

  public isf: IsfModel;
  public formGroup: UntypedFormGroup;
  public displayErrors: Map<string, string> = new Map<string, string>();
  public formErrors: { [name: string]: IErrorForm };

  public consolidatorDisplayFields: ISectionFormField[] = [];
  public enumEntityModelCode: typeof enumEntityModelCode = enumEntityModelCode;
  public enumEntityType: typeof enumEntityType = enumEntityType;
  public isEditMode: boolean = false;
  public isDesktop: boolean = false;
  public seller: EntityModel;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private fh: FormHelper,
    private cdr: ChangeDetectorRef,
    private genericService: GenericService,
    private shipmentsV2Repository: ShipmentsV2Repository,
    private isfOcrQuickFixService: IsfOcrQuickFixService,
    private isfStateService: IsfStateService,
  ) {}

  ngOnInit(): void {
    const isf = this.isfOcrQuickFixService.getIsf();
    this.syncIsf(isf);

    this.initializeFormGroup();

    this.isEditMode = this.isAnyFormBlockFieldEmpty(this.consolidatorFields, this.formGroup);

    this.fh.updateEntityValidation(true, this.formGroup);

    this.genericService.isDesktop$.pipe(takeUntil(this.destroy$)).subscribe((isDesktop) => {
      this.isDesktop = isDesktop;
    });

    this.setFieldsProperties();

    this.populateDisplayErrors(this.formGroup);
    this.createFormErrors();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public isAnyFormBlockFieldEmpty(blockFields: ISectionFormField[], fg: UntypedFormGroup): boolean {
    let isFgInvalid: boolean = false;
    Object.keys(fg.controls).forEach((key) => {
      if (fg.controls[key].invalid) {
        isFgInvalid = true;
        return;
      }
    });

    let invalidFields = blockFields.find((field) => fg.get(field.name)?.invalid || !fg.get(field.name)?.value || isFgInvalid);
    return !!invalidFields;
  }

  public onBlockEdit(isShowEditBlock: boolean) {
    this.isEditMode = isShowEditBlock;
    this.formGroup.markAllAsTouched();
    this.formGroup.updateValueAndValidity();
  }

  public onSubmit(isNeedCheck: boolean = true): void {
    const consolidatorInvalidFields = this.fh.getInvalidFieldsByCode(this.formGroup.value, enumEntityModelCode.SELLER); //Set as seller instead of consolidator due to old code validation rules
    if (isNeedCheck && Object.keys(consolidatorInvalidFields).length > 0) {
      this.fh.markAsTouched(this.formGroup);
      this.openMissingFieldsModal(consolidatorInvalidFields);
      return;
    }

    this.updateOrCreateConsolidator()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isf) => {
        this.isfOcrQuickFixService.setIsf(isf);
        this.isfStateService.updateCurrentIsf(isf);
        this.dataSaved.emit();
      });
  }

  public onClearForm(): void {
    this.formGroup.reset();
  }

  private openMissingFieldsModal(data) {
    this.missingFieldsModal.openCustomModal(data);
  }

  private setFieldsProperties() {
    const consolidator = this.isf?.shipment?.consolidator;

    const addressFieldMissingPartsErrorText = this.addressFieldMissingPartsErrorText();
    const consolidatorAddressDisplayLine = !!addressFieldMissingPartsErrorText ? '' : this.getAddressLines(consolidator?.address);
    const nameField = this.consolidatorFields.find((field) => field.name === 'name');

    this.consolidatorDisplayFields.push(
      {
        ...nameField,
        type: 'text',
        label: 'LABELS.NAME',
        name: 'name',
        value: consolidator?.name,
      },
      {
        type: 'text',
        label: 'LABELS.ADDRESS',
        name: 'address',
        value: consolidatorAddressDisplayLine,
      },
    );

    this.cdr.markForCheck();
  }

  private populateDisplayErrors(fg: UntypedFormGroup) {
    Object.keys(fg.controls).forEach((fieldName) => {
      switch (fieldName) {
        case 'name':
          this.displayErrors.set(fieldName, this.fieldMissingErrorText(fieldName, fg));
          break;
        case 'address':
          this.displayErrors.set(fieldName, this.addressFieldMissingPartsErrorText());
          break;
        default:
          break;
      }
    });
  }

  private createFormErrors(): void {
    this.formErrors = this.fh.createErrorsForIsfFormGroup();
  }

  private fieldMissingErrorText(fieldName: string | string[], fg: UntypedFormGroup): string {
    return !fg.get(fieldName)?.value ? 'LABELS.MISSING' : '';
  }

  private addressFieldMissingPartsErrorText(): string {
    const consolidatorAddressErrors = this.isf?.shipment?.consolidator?.address?.validationMessages || {};
    const missingAddressPartsText = !UtilsHelper.isObjectEmpty(consolidatorAddressErrors) ? 'BACK_ERROR.MISSING_ADDRESS_PARTS' : '';
    return missingAddressPartsText;
  }

  private getAddressLines(data: AddressModel): string {
    if (!data) {
      return '';
    }

    const state = data.state ? data.state.description : null;
    const country = data.country ? data.country.description : null;
    const address = [data?.line1, data?.line2, data?.line3]
      .filter((part) => !!part)
      .join('\n')
      .trim();

    const line1 = [address, data.city]
      .filter((part) => !!part)
      .join(', ')
      .trim();
    const line2 = [country, state]
      .filter((part) => !!part)
      .join(', ')
      .trim();

    return line1 + line2;
  }

  private updateOrCreateConsolidator() {
    const consolidator: EntityModel = this.formGroup.value;
    const consolidatorId = this.formGroup.get('id').value;

    let newShipmentData: Partial<ShipmentModel>;

    if (consolidatorId === this.seller?.id) {
      newShipmentData = {
        ...this.isf.shipment,
        consolidator: this.seller,
        consolidatorId: this.seller?.id,
      };
    } else {
      newShipmentData = {
        ...this.isf.shipment,
        consolidator,
        consolidatorId,
      };
    }

    return of(consolidator).pipe(
      switchMap((entity) => {
        if (!consolidatorId) {
          entity.addressId = 0;
          entity.address.id = 0;
          entity.entityCode = enumEntityModelCode.CONSOLIDATOR;
          return this.isfStateService.createEntity(entity);
        } else {
          return this.isfStateService.updateEntity(entity);
        }
      }),
      switchMap((entity) => {
        return this.updateShipment({
          ...newShipmentData,
          consolidator: entity,
          consolidatorId: entity.id,
        });
      }),
      switchMap((newShipment) => this.isfStateService.updateShipment(newShipment)),
      switchMap((shipment) => {
        return this.isfStateService.updateIsf({
          ...this.isf,
          shipment,
        });
      }),
      switchMap((isf) => this.isfStateService.loadIsf(isf.referenceNumber)),
      tap((isf) => {
        this.syncIsf(isf);
      }),
    );
  }

  private updateShipment(newShipmentData?: Partial<ShipmentModel>) {
    if (!newShipmentData) {
      newShipmentData = { ...this.isf?.shipment };
    }

    return this.shipmentsV2Repository.update(this.isf?.shipment?.id, newShipmentData);
  }

  private initializeFormGroup() {
    this.formGroup = this.fh.createEntityModelFormGroup(enumEntityModelCode.CONSOLIDATOR);

    this.formGroup.patchValue(this.isf?.shipment?.consolidator);

    this.formGroup
      .get('id')
      .valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged(), startWith(this.formGroup.get('id').value))
      .subscribe((consolidatorId) => {
        this.applySellerOrConsolidator(consolidatorId);
      });

    merge(this.formGroup.get('name').valueChanges, this.formGroup.get('address').valueChanges)
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((_) => {
        if (!this.formGroup.pristine && this.seller && this.formGroup.get('id').value === this.seller.id) {
          this.formGroup.get('id').patchValue(this.isf?.shipment?.consolidator?.id || 0);
        }
      });

    if (this.formGroup.invalid) {
      this.formGroup.markAsDirty();
      this.formGroup.markAllAsTouched();
    }
  }

  private applySellerOrConsolidator(id: number) {
    if (this.seller && id === this.seller.id) {
      this.formGroup.markAsPristine();
      this.fh.patchIsfEntity(this.formGroup, this.seller);
    } else {
      this.fh.patchIsfEntity(this.formGroup, this.isf?.shipment?.consolidator);
    }
  }

  private syncIsf(isf: IsfModel) {
    this.isf = isf;
    this.seller = this.isf?.shipment?.seller;
  }
}
